/**
 * Planning item that can be the current work of its machine
 */
export interface CurrentWorkCandidate {
  /** The item heads the punch queue of its machine (computed by the console, see PlanningQueueHelper) */
  isQueueHead: boolean;
  isFinished: boolean;
  displayStart: Date | null;
}

/**
 * Single definition of the work in progress on a machine, shared by operations and machine events.
 * Mirrors the server rule (isQueueItemTheoreticallyInProgress in planning-queue.helper): the first unfinished
 * item of the punch queue, once its planned start has passed, even when it overruns its planned end.
 * An item planned in the future is not highlighted. The same cases are pinned on both sides by
 * planning-current.helper.spec.ts and planning-queue.helper.test.js.
 */
export class PlanningCurrentHelper {
  /**
   * Whether an item is the current work of its machine
   * @param item Operation or machine event
   * @param at Instant to test
   * @returns True when the item heads the punch queue and has started
   */
  static isCurrent(item: CurrentWorkCandidate, at: Date): boolean {
    if (!item.isQueueHead || item.isFinished || !item.displayStart) {
      return false;
    }
    return item.displayStart.getTime() <= at.getTime();
  }

  /**
   * Whether a delay can be declared on an item: it must really be under way, so it is the current work
   * @param item Operation
   * @param at Instant to test
   * @returns True when the item is the current work
   */
  static canDeclareDelay(item: CurrentWorkCandidate, at: Date): boolean {
    return PlanningCurrentHelper.isCurrent(item, at);
  }
}
